import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Pencil, Lock } from "lucide-react";
import { baseProgramName } from "@/lib/programs";
import { AddTeamForm, AddTrainerForm, AddEventForm } from "@/pages/Add";

/**
 * "Edit info" button + modal for community/wiki editing. Reuses the exact same
 * create forms (Add Team / Add Trainer / Add Event) in edit mode so the layout,
 * field placement and photo uploaders match the Add pages one-to-one. Title and
 * photos are fully editable here.
 */
export default function WikiEditModal({ type, entity, onSaved, autoOpen = false }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [showNotice, setShowNotice] = useState(false);
    const [conflicts, setConflicts] = useState([]);
    const [reference, setReference] = useState("");

    // Editing is restricted once a listing is claimed — only the owner/admin may edit.
    const claimed = !!entity.owner_verified;
    const isOwner = !!user && (user.is_admin || entity.owner_user_id === user.id);
    const restricted = claimed && !isOwner;
    const canEdit = !!user && !restricted;
    const notice = restricted
        ? "Owner of this page has restricted editing."
        : "You must be logged in to make edits to this listing.";

    // Source-locked (admin/system/trusted) listing under 10 followers, not yet claimed, and the
    // viewer isn't a full editor → known-accurate info is add-only until 10 followers.
    const fullEditor = !!user && (user.is_admin || user.is_content_admin || user.is_trusted_lister || entity.owner_user_id === user.id);
    const lockNote = !!entity.source_locked && !claimed && !fullEditor
        && (entity.follower_count == null || entity.follower_count < 10)
        && (type === "team" || type === "trainerListing");
    const entityKey = type === "team" ? "team" : "trainer_listing";

    // Deep-link support: /trainer-listings/:id?edit=1 (or event) auto-opens the editor for the owner.
    useEffect(() => { if (autoOpen && canEdit) setOpen(true); }, [autoOpen, canEdit]);

    const onEditClick = () => { if (canEdit) setOpen(true); else setShowNotice((v) => !v); };
    const finish = (savedConflicts) => {
        setOpen(false);
        if (Array.isArray(savedConflicts) && savedConflicts.length) setConflicts(savedConflicts);
        else onSaved?.(entity);
    };
    const submitReview = async () => {
        try {
            await api.post(`/listings/${entityKey}/${entity.id}/change-request`, { changes: conflicts, reference: reference.trim() });
            toast.success("Sent to admins for review — you'll be notified of the decision.");
        } catch {
            toast.error("Couldn't submit your change for review. Please try again.");
        }
        setConflicts([]); setReference(""); onSaved?.(entity);
    };
    const dismissReview = () => { setConflicts([]); setReference(""); onSaved?.(entity); };

    // Teams: load the whole program (all rosters) so the modal edits every roster —
    // same as the owner edit experience.
    const [programRosters, setProgramRosters] = useState(null);
    useEffect(() => {
        if (!open || type !== "team") return;
        setProgramRosters(null);
        api.get(`/teams/${entity.id}/program`)
            .then(({ data }) => setProgramRosters(Array.isArray(data) && data.length ? data : [entity]))
            .catch(() => setProgramRosters([entity]));
    }, [open, type, entity]);

    const wide = type === "team" ? "max-w-5xl" : "max-w-3xl";

    return (
        <>
            <span className="relative inline-flex group" onMouseLeave={() => setShowNotice(false)}>
                <button
                    type="button"
                    data-testid={`wiki-edit-${type}-btn`}
                    onClick={onEditClick}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#2a2a2a] text-[10px] uppercase tracking-[0.2em] text-white hover:border-[#ff4500] hover:text-[#ff4500] transition-colors"
                >
                    <Pencil className="h-3 w-3" /> Edit info
                </button>
                {!canEdit && (
                    <span
                        data-testid={`wiki-edit-${type}-notice`}
                        className={`absolute left-0 top-full mt-2 z-[60] w-60 px-3 py-2 bg-[#0f0f0f] border border-[#ff4500] text-[10px] leading-relaxed tracking-normal normal-case text-[#e5e5e5] shadow-xl transition-opacity duration-150 ${showNotice ? "opacity-100 visible" : "opacity-0 invisible group-hover:opacity-100 group-hover:visible"}`}
                    >
                        {notice}
                    </span>
                )}
            </span>
            {open && (
                <div className="fixed inset-0 z-[1300] bg-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid="wiki-edit-modal">
                    <div className={`w-full ${wide} bg-[#141414] border border-[#2a2a2a] my-6`}>
                        <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between gap-3 sticky top-0 bg-[#141414] z-10">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Edit info · anyone can improve this listing</span>
                            <button type="button" onClick={() => setOpen(false)} className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-white shrink-0" data-testid="wiki-close">Close</button>
                        </div>
                        <div className="p-4">
                            {lockNote && (
                                <div data-testid="wiki-lock-note" className="mb-4 flex items-start gap-2 border border-[#a855f7]/40 bg-[#a855f7]/10 px-3 py-2.5 text-[12px] leading-relaxed text-[#e5d4ff]">
                                    <Lock className="h-4 w-4 mt-0.5 shrink-0 text-[#a855f7]" />
                                    <span>This listing's original info is verified and fully editable once it reaches <strong>10 followers</strong>. You can <strong>add</strong> missing info now, but existing info can't be changed or removed yet. If something is inaccurate, edit it and save — we'll ask for a source and send it to an admin for review.</span>
                                </div>
                            )}
                            {type === "team" && (programRosters
                                ? <AddTeamForm sport={entity.sport} existing={{ base_name: baseProgramName(entity), rosters: programRosters }} onDone={finish} />
                                : <div className="py-10 text-center text-[#a0a0a0] text-sm" data-testid="wiki-team-loading">Loading teams…</div>)}
                            {type === "trainerListing" && <AddTrainerForm sport={entity.sport} existing={entity} onDone={finish} />}
                            {type === "event" && <AddEventForm sport={entity.sport} existing={entity} onDone={finish} />}
                        </div>
                    </div>
                </div>
            )}
            {conflicts.length > 0 && (
                <div className="fixed inset-0 z-[1400] bg-black/80 flex items-center justify-center p-4" data-testid="locked-conflict-dialog">
                    <div className="w-full max-w-md bg-[#141414] border border-[#a855f7]/50 p-5">
                        <div className="flex items-center gap-2 text-[#a855f7] text-xs uppercase tracking-[0.2em] mb-3">
                            <Lock className="h-4 w-4" /> Locked info — needs review
                        </div>
                        <p className="text-sm text-[#cfcfcf] leading-relaxed mb-3">
                            These fields are verified and locked until the listing reaches 10 followers, so your change to
                            {" "}<strong className="text-white">{conflicts.map((c) => c.field).join(", ")}</strong>{" "}
                            wasn't applied. Paste a link (website, article, official page) that backs up your change and we'll send it to an admin to review.
                        </p>
                        <input
                            data-testid="conflict-reference-input"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder="https://source-that-proves-the-change.com"
                            className="w-full bg-[#0f0f0f] border border-[#2a2a2a] px-3 py-2 text-sm text-white outline-none focus:border-[#a855f7] mb-4"
                        />
                        <div className="flex gap-2 justify-end">
                            <button type="button" onClick={dismissReview} data-testid="conflict-cancel" className="px-4 py-2 text-[11px] uppercase tracking-wider border border-[#2a2a2a] text-[#a0a0a0] hover:text-white">Skip</button>
                            <button type="button" onClick={submitReview} disabled={!reference.trim()} data-testid="conflict-submit" className="px-4 py-2 text-[11px] uppercase tracking-wider bg-[#a855f7] text-white disabled:opacity-40 hover:bg-[#9333ea]">Send for review</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
